import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Typography, Button, Layout, Col, Row, message, Modal } from 'antd';
import DefaultTemplate from '../../templates/DefaultTemplate';
import DoubleTemplate from '../../templates/DoubleTemplate';
import TripleTemplate from '../../templates/TripleTemplate';
import QuadrupleTemplate from '../../templates/QuadrupleTemplate';
import TwoThirdTemplate from '../../templates/TwoThirdTemplate';
import { fetchSinglePage, clearSinglePage, savePageContents, updatePageSettings } from '../../../actions/singlePage.actions';
import { fetchSingleTemplate, clearSingleTemplate } from '../../../actions/templates.actions';
import PageForm from '../pages/PageForm';
import NotFound from '../NotFound';
import { authenticationUtils, logUtils } from '../../../utils/common';

import { publishConfig } from '../../../config/env';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const Edit = props => {
    const [pageSettingsModalVisible, setPageSettingsModalVisible] = useState(false);
    const { pageSettingsUpdating, pageSettingsUpdateSuccess, pageSettingsUpdateFailed, updatedPagePath, savePageContentsSuccess } = props;
    const { page, location, fetchSinglePage, fetchSingleTemplate } = props;
    const { clearSinglePage, clearSingleTemplate } = props;

    const [token, setToken] = useState();
    const [isPublishing, setIsPublishing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [pageNotFound, setPageNotFound] = useState(false);

    useEffect(() => {
        message.config({
            maxCount: 1
        });
        authenticationUtils.getUserToken().then(setToken);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** When: URL changed **/
    useEffect(() => {
        fetchSinglePage(getPathArrFromURL(location));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, fetchSinglePage]);

    /** When: page fetched **/
    useEffect(() => {
        const pageReceived = !(Object.keys(page).length === 0 && page.constructor === Object);
        if (pageReceived) {
            if (page.found) {
                fetchSingleTemplate(page.template);
            } else {
                setPageNotFound(true);
            }
        }
    }, [page, fetchSingleTemplate]);

    /** When: page settings update done  **/
    useEffect(() => {
        if (pageSettingsUpdating === false) {
            if (pageSettingsUpdateSuccess === true) {
                setPageSettingsModalVisible(false);
                props.history.push('/edit' + updatedPagePath);
                message.success('Updated the page-settings.');
            } else if (pageSettingsUpdateFailed === true) {
                setPageSettingsModalVisible(false);
                message.error('Update of the page-settings failed, please try again.');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSettingsUpdating, pageSettingsUpdateSuccess, pageSettingsUpdateFailed, updatedPagePath, props.history]);

    /** When: Page save done **/
    useEffect(() => {
        if (savePageContentsSuccess === true) {
            setIsSaving(false);
            message.success('Page successfully saved.');
        } else if (savePageContentsSuccess === false) {
            //can also be undefined.
            setIsSaving(false);
            message.error('Page could not be saved.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savePageContentsSuccess]);

    /** When: Unmount **/
    useEffect(() => {
        return () => {
            clearSinglePage();
            clearSingleTemplate();
        };
    }, [clearSinglePage, clearSingleTemplate]);

    const saveTemplateLayout = () => {
        setIsSaving(true);
        message.loading('Page is being saved.');
        props.savePageContents(props.page);
    };

    const getTemplate = () => {
        const templateReceived = !(Object.keys(props.template).length === 0 && props.template.constructor === Object);

        if (templateReceived) {
            switch (props.template.id) {
                case 'Default':
                    return <DefaultTemplate editMode={true} title={page.title} />;
                case 'Double':
                    return <DoubleTemplate editMode={true} title={page.title} />;
                case 'Triple':
                    return <TripleTemplate editMode={true} title={page.title}/>;
                case 'Quadruple':
                    return <QuadrupleTemplate editMode={true} title={page.title}/>;
                case 'TwoThird':
                    return <TwoThirdTemplate editMode={true} title={page.title}/>;
                default: /* do nothing */
            }
        }
        return '';
    };

    const publishPage = () => {
        if (token !== undefined) {
            setIsPublishing(true);
            message.loading('Page is being published.');
            fetch(publishConfig.url, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'access-control-allow-origin': '*',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    path: props.page.path
                })
            })
                .then(res => {
                    if (res.status === 200) {
                        handlePublishSuccess();
                    } else {
                        handlePublishError();
                    }
                })
                .catch(handlePublishError);
        }
    };
    const handlePublishSuccess = () => {
        setIsPublishing(false);

        logUtils.logAuthorAction(logUtils.AUTHOR_ACTIONS.PAGE_PUBLISHED, page);
        message.success('Page successfully published.');
    };
    const handlePublishError = () => {
        setIsPublishing(false);
        message.error('Page could not be published.');
    };

    const openPreview = () => {
        let path = props.location.pathname;
        var pathArr = path.split('/');
        let index = pathArr.findIndex(entry => entry === 'edit');
        pathArr[index] = 'view';

        window.open(pathArr.join('/'));
    };

    const showPageSettings = () => {
        setPageSettingsModalVisible(true);
    };

    const onPageSettingsOk = data => {
        const newPageData = {
            title: data.title,
            description: data.description,
            headerImage: data.headerImage,
            headerImageAlt: data.headerImageAlt,
            path: data.path,
            parentPath: data.parentPath
        };
        const payload = { page: page, newPageData: newPageData, index: props.index };
        props.updatePageSettings(payload);
    };

    const onPageSettingsCancel = () => {
        setPageSettingsModalVisible(false);
    };

    const getPathArrFromURL = location => {
        const pathArr = location.pathname.split('/');
        pathArr.splice(0, 2); //Remove Edit

        return pathArr;
    };

    if (pageNotFound) {
        return <NotFound></NotFound>;
    } else {
        return (
            <Layout className="layout">
                <Header className="header" style={{ background: 'none', padding: 0 }}>
                    <Row type="flex" justify="space-between">
                        <Col span={18}>
                            <Title level={1} className="pageTitle">
                                Editing
                            </Title>
                            <Text type="secondary" className="pageTitle--subscript">
                                {props.page.title}
                            </Text>
                        </Col>
                        <Col>
                            <Button.Group>
                                <Button type="warning" onClick={showPageSettings} icon="setting" title="Settings" />
                                <Button type="primary" onClick={openPreview} icon="eye" title="Preview" />
                                <Button type="primary" onClick={saveTemplateLayout} icon="save" title="Save" disabled={isSaving} />
                                <Button type="success" onClick={publishPage} icon="cloud-upload" title="Publish" disabled={isPublishing} />
                            </Button.Group>
                        </Col>
                    </Row>
                    <Modal title="Page settings" destroyOnClose={true} visible={pageSettingsModalVisible} onCancel={onPageSettingsCancel} footer={null}>
                        <PageForm onOk={onPageSettingsOk} onCancel={onPageSettingsCancel} {...page} isEditing={true} />
                    </Modal>
                </Header>
                <Content className="content">{getTemplate()}</Content>
            </Layout>
        );
    }
};

const mapStateToProps = state => ({
    i18n: state.i18n,

    page: state.page.page,

    pageSettingsUpdating: state.page.pageSettingsUpdating,
    pageSettingsUpdateSuccess: state.page.pageSettingsUpdateSuccess,
    pageSettingsUpdateFailed: state.page.pageSettingsUpdateFailed,

    savePageContentsSuccess: state.page.savePageContentsSuccess,

    updatedPagePath: state.page.updatedPagePath,

    template: state.templates.template,

    index: state.pages.index
});

const mapDispatchToProps = {
    fetchSinglePage,
    clearSinglePage,
    savePageContents,
    updatePageSettings,
    fetchSingleTemplate,
    clearSingleTemplate
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Edit);
